import React, { useMemo } from 'react';
import { Autocomplete, TextField, TextFieldProps } from '@mui/material';

/** Passo dos horários da agenda: 5 em 5 minutos, igual para toda clínica (decisão do Hilton, 01/10/2026). */
export const TIME_STEP_MINUTES = 5;

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const fromMinutes = (total: number): string =>
  `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;

const ALL_TIMES: string[] = Array.from(
  { length: (24 * 60) / TIME_STEP_MINUTES },
  (_, i) => fromMinutes(i * TIME_STEP_MINUTES),
);

export const durationLabel = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
};

// Digitar filtra como HORÁRIO, não como texto (senão "14" traria 01:40): 1 dígito = começo da hora
// ("9" → 09h; "1" → 01h e 10h a 19h), 2 = a hora ("14"), 3 = hora + começo do minuto ("930" → 09:30, "143" → 14:30 e 14:35),
// 4 = o horário ("1430"). Separadores são ignorados ("14:30").
const matchesDigits = (time: string, digits: string): boolean => {
  const hour = time.slice(0, 2);
  const minute = time.slice(3, 5);
  switch (digits.length) {
    case 1:
      return hour.startsWith(digits) || hour === `0${digits}`;
    case 2:
      return hour === digits;
    case 3:
      return (hour === digits.slice(0, 2) && minute.startsWith(digits[2]))
        || (hour === `0${digits[0]}` && minute === digits.slice(1));
    default:
      return hour + minute === digits.slice(0, 4);
  }
};

export const filterByDigits = (options: string[], state: { inputValue: string }): string[] => {
  const digits = state.inputValue.replace(/\D/g, '');
  return digits ? options.filter((t) => matchesDigits(t, digits)) : options;
};

/** Faixa de atendimento da clínica (RN-118o), como vem em /schedules/options. */
export interface BusinessHoursRange {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

/** Janela da lista: [início, fim] em minutos; null = dia inteiro. */
export type TimeWindow = { start: number; end: number } | null;

export const WINDOW_BEFORE_MINUTES = 2 * 60;
export const WINDOW_AFTER_MINUTES = 3 * 60;

/**
 * A lista começa 2 h antes do primeiro horário de atendimento da semana e termina 3 h depois do
 * último (decisão do Hilton, 01/10/2026): limita a rolagem sem esconder o que a clínica usa.
 * Sem horário cadastrado, o dia inteiro.
 */
export const timeWindowOf = (businessHours?: BusinessHoursRange[] | null): TimeWindow => {
  if (!businessHours || businessHours.length === 0) return null;
  const start = Math.min(...businessHours.map((h) => h.startMinute));
  const end = Math.max(...businessHours.map((h) => h.endMinute));
  return {
    start: Math.max(0, start - WINDOW_BEFORE_MINUTES),
    end: Math.min(24 * 60, end + WINDOW_AFTER_MINUTES),
  };
};

/**
 * Horários oferecidos: passo de 5 min dentro da janela; no FIM, só depois do início; valor já
 * gravado fora do passo ou da janela continua na lista, para não sumir do campo.
 */
export const timeOptions = (after?: string, value?: string, window: TimeWindow = null): string[] => {
  const afterMin = after ? toMinutes(after) : -1;
  const list = ALL_TIMES.filter((t) => {
    const m = toMinutes(t);
    return m > afterMin && (!window || (m >= window.start && m <= window.end));
  });
  return value && !list.includes(value) ? [...list, value].sort() : list;
};

export interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  /**
   * Campo de FIM: só oferece horários depois deste início e mostra a duração ao lado
   * ("15:00 · 1h"). Vazio = campo de início, o dia inteiro.
   */
  after?: string;
  /** Horário de atendimento da clínica: recorta a lista (2 h antes, 3 h depois). Vazio = dia inteiro. */
  businessHours?: BusinessHoursRange[] | null;
  size?: 'small' | 'medium';
  sx?: TextFieldProps['sx'];
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  inputAriaLabel?: string;
}

/**
 * Horário da agenda escolhido numa LISTA de 5 em 5 minutos, igual em todo navegador e no celular
 * (o relógio nativo aceitava qualquer minuto e cada aparelho o mostrava de um jeito). Abre perto do
 * valor atual e filtra enquanto se digita. Valor antigo fora do passo continua aparecendo como está.
 */
const TimeSelect: React.FC<TimeSelectProps> = ({
  value, onChange, label, disabled, error, helperText, after, businessHours, size, sx, InputLabelProps, inputAriaLabel,
}) => {
  const window = useMemo(() => timeWindowOf(businessHours), [businessHours]);
  // Agendamento antigo com minuto fora do passo (ex.: 07:59) continua na lista para não sumir do campo
  const options = useMemo(() => timeOptions(after, value, window), [after, value, window]);

  return (
    <Autocomplete
      options={options}
      // Sem o botão de limpar: horário não fica vazio, e o botão roubava a largura do campo estreito
      disableClearable
      value={(value || null) as unknown as string}
      onChange={(_, next) => { if (next) onChange(next); }}
      filterOptions={filterByDigits}
      getOptionLabel={(t) => t}
      renderOption={(props, t) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest} style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {t}
            {after && (
              <span style={{ marginLeft: '0.5rem', color: '#6c757d', fontSize: '0.85em' }}>
                · {durationLabel(toMinutes(t) - toMinutes(after))}
              </span>
            )}
          </li>
        );
      }}
      disabled={disabled}
      autoHighlight
      autoSelect
      openOnFocus
      size={size}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          InputLabelProps={{ ...params.InputLabelProps, ...InputLabelProps }}
          inputProps={{ ...params.inputProps, inputMode: 'numeric', 'aria-label': inputAriaLabel ?? label }}
          sx={[
            // Reserva só a seta à direita; o horário (5 caracteres) nunca é cortado com reticências
            {
              '& .MuiAutocomplete-inputRoot': { paddingRight: '30px !important' },
              '& .MuiAutocomplete-input': { minWidth: '5ch !important', textOverflow: 'clip', fontVariantNumeric: 'tabular-nums' },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        />
      )}
    />
  );
};

export default TimeSelect;
